import { useEffect, useMemo } from 'react';

import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useResourceNavigation } from './useResourceNavigation';

interface UseEditPageHeaderOptions {
	id?: string | number;
	resourceName: string;
	entityDisplayName?: string;
	description?: string;
	disableBackButton?: boolean;
}

interface UseEditPageHeaderResult {
	navigateToList: () => void;
	title: string;
	description: string;
}

const humaniseResourceName = (value: string) => {
	return value
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/[-_]+/g, ' ')
		.split(/\s+/)
		.filter(Boolean)
		.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
};

export const useEditPageHeader = ({
	id,
	resourceName,
	entityDisplayName,
	description,
	disableBackButton = false
}: UseEditPageHeaderOptions): UseEditPageHeaderResult => {
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const { goToList } = useResourceNavigation(resourceName);

	const navigateToList = goToList;

	const entityLabel = useMemo(() => {
		const customLabel = entityDisplayName?.trim();
		if (customLabel) {
			return customLabel;
		}

		const derivedLabel = humaniseResourceName(resourceName);
		return derivedLabel || 'Record';
	}, [entityDisplayName, resourceName]);

	const headerTitle = useMemo(
		() => `Edit ${entityLabel}`,
		[entityLabel]
	);

	const headerDescription = useMemo(() => {
		if (description) {
			return description;
		}

		const base = `Update ${entityLabel.toLowerCase()} details`;

		if (id != null && String(id).trim().length > 0) {
			return `${base} (ID: ${id})`;
		}

		return base;
	}, [description, entityLabel, id]);

	useEffect(() => {
		setPageHeader({
			title: headerTitle,
			description: headerDescription,
			showBackButton: !disableBackButton,
			onBackClick: !disableBackButton ? navigateToList : undefined
		});

		return () => {
			resetPageHeader();
		};
	}, [
		setPageHeader,
		resetPageHeader,
		headerTitle,
		headerDescription,
		disableBackButton,
		navigateToList
	]);

	return {
		navigateToList,
		title: headerTitle,
		description: headerDescription
	};
};
